import { c as _c } from "react/compiler-runtime";
// Tremor Raw Switch [v0.0.0]
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { VariantProps } from "tailwind-variants";
import React from "react";
import { tv } from "@/lib/tailwind-utils";
import { cx, focusRing } from "@/lib/tailwind-utils";
const switchVariants = tv({
  slots: {
    root: [
    // base
    "group relative isolate inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 shadow-inner outline-none ring-1 ring-inset transition-all", "bg-muted",
    // ring color
    "ring-border/5",
    // checked
    "data-[state=checked]:bg-primary",
    // disabled
    "data-[disabled]:cursor-default",
    // disabled checked
    "data-[disabled]:data-[state=checked]:bg-primary/40", "data-[disabled]:data-[state=checked]:ring-primary/60",
    // disabled unchecked
    "data-[disabled]:data-[state=unchecked]:ring-border", "data-[disabled]:data-[state=unchecked]:bg-muted", focusRing],
    thumb: [
    // base
    "pointer-events-none relative inline-block transform appearance-none rounded-full border-none shadow-lg outline-none transition-all duration-150 ease-in-out focus:border-none focus:outline-none focus:outline-transparent",
    // background color
    "bg-background/80 backdrop-blur-sm",
    // disabled
    "group-data-[disabled]:shadow-none", "group-data-[disabled]:bg-background-secondary"]
  },
  variants: {
    size: {
      default: {
        root: "h-5 w-9",
        thumb: "h-4 w-5 group-data-[state=checked]:translate-x-3 group-data-[state=unchecked]:translate-x-0"
      },
      small: {
        root: "h-4 w-7",
        thumb: "h-3 w-4 group-data-[state=checked]:translate-x-2 group-data-[state=unchecked]:translate-x-0"
      }
    }
  },
  defaultVariants: {
    size: "default"
  }
});
interface SwitchProps extends Omit<React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>, "asChild">, VariantProps<typeof switchVariants> {
  indeterminate?: boolean;
}
const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>((t0, forwardedRef) => {
  const $ = _c(36);
  let className;
  let indeterminate;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      indeterminate,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = indeterminate;
    $[3] = props;
    $[4] = size;
  } else {
    className = $[1];
    indeterminate = $[2];
    props = $[3];
    size = $[4];
  }
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[5] !== className || $[6] !== forwardedRef || $[7] !== indeterminate || $[8] !== props || $[9] !== size) {
    const {
      root,
      thumb
    } = switchVariants({
      size
    });
    const t8 = props as SwitchProps & {
      onCheckedChange?: (checked: boolean) => void;
    };
    let checked;
    let id;
    let onCheckedChange;
    let rest;
    if ($[19] !== t8) {
      ({
        id,
        checked,
        onCheckedChange,
        ...rest
      } = t8);
      $[19] = t8;
      $[20] = checked;
      $[21] = id;
      $[22] = onCheckedChange;
      $[23] = rest;
    } else {
      checked = $[20];
      id = $[21];
      onCheckedChange = $[22];
      rest = $[23];
    }
    T1 = SwitchPrimitives.Root;
    t2 = forwardedRef;
    t3 = cx(root(), indeterminate && "bg-primary/50", className);
    t4 = id;
    t5 = checked;
    t6 = onCheckedChange;
    t7 = rest as any;
    T0 = SwitchPrimitives.Thumb;
    t1 = cx(thumb(), indeterminate && (size === "small" ? "translate-x-1 group-data-[state=unchecked]:translate-x-1" : "translate-x-1.5 group-data-[state=unchecked]:translate-x-1.5"));
    $[5] = className;
    $[6] = forwardedRef;
    $[7] = indeterminate;
    $[8] = props;
    $[9] = size;
    $[10] = T0;
    $[11] = T1;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    T0 = $[10];
    T1 = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
  }
  let t8;
  if ($[24] !== T0 || $[25] !== t1) {
    t8 = <T0 className={t1} />;
    $[24] = T0;
    $[25] = t1;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== T1 || $[28] !== t2 || $[29] !== t3 || $[30] !== t4 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8) {
    t9 = <T1 ref={t2} className={t3} id={t4} checked={t5} onCheckedChange={t6} {...t7}>{t8}</T1>;
    $[27] = T1;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
});
Switch.displayName = "Switch";
export default Switch;